import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../api/client";
import { COLORS } from "../theme";
import Card from "../components/ui/Card";
import Btn from "../components/ui/Btn";
import Input from "../components/ui/Input";
import Badge from "../components/ui/Badge";
import Modal from "../components/ui/Modal";
import Avatar from "../components/ui/Avatar";
import EstadoCarregando from "../components/ui/EstadoCarregando";
import EstadoVazio from "../components/ui/EstadoVazio";
import { ThFiltroTexto, ThFiltroSelect, ThSimples, BarraResumoFiltro } from "../components/ui/ThFiltro";

const VAZIO = { nome: "", email: "", senha: "", perfil: "funcionario" };
const PERFIL_TONE = { gestor: "success", funcionario: "neutral", admin_empresa: "warning" };
const PERFIL_LABEL = { gestor: "Gestor", funcionario: "Funcionário", admin_empresa: "Administrador" };

export default function Usuarios() {
  const { token } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  // Preenchido só quando o e-mail conflita com alguém da MESMA empresa —
  // dá pra linkar direto pro cadastro dessa pessoa (ver backend/lib/emailConflito.js).
  const [conflito, setConflito] = useState(null);

  // Trocar perfil de alguém já cadastrado (ex: tirar de Gestor e deixar só
  // Funcionário) — antes só dava pra escolher o perfil na hora de criar.
  const [editandoPerfil, setEditandoPerfil] = useState(null); // usuário sendo editado
  const [novoPerfil, setNovoPerfil] = useState("funcionario");
  const [novoPodeAdmin, setNovoPodeAdmin] = useState(false);
  const [salvandoPerfil, setSalvandoPerfil] = useState(false);

  const [filtroNome, setFiltroNome] = useState("");
  const [filtroEmail, setFiltroEmail] = useState("");
  const [filtroPerfil, setFiltroPerfil] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("");

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch("GET", "/api/usuarios", undefined, token);
      setUsuarios(data);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async () => {
    setErro("");
    setConflito(null);
    if (!form.nome || !form.email || !form.senha) {
      setErro("Preencha nome, e-mail e senha");
      return;
    }
    setSalvando(true);
    try {
      await apiFetch("POST", "/api/usuarios", form, token);
      setModalOpen(false);
      setForm(VAZIO);
      carregar();
    } catch (e) {
      setErro(e.message);
      if (e.data?.mesma_empresa && e.data?.funcionario_id) {
        setConflito({ funcionarioId: e.data.funcionario_id, nome: e.data.nome_conflito });
      }
    } finally {
      setSalvando(false);
    }
  };

  const alternarAtivo = async (u) => {
    try {
      await apiFetch("PUT", `/api/usuarios/${u.id}`, { ativo: !u.ativo }, token);
      carregar();
    } catch (e) {
      window.alert(e.message);
    }
  };

  const resetarSenha = async (u) => {
    const senha = window.prompt(`Nova senha para ${u.nome}:`);
    if (!senha) return;
    try {
      await apiFetch("POST", `/api/usuarios/${u.id}/resetar-senha`, { senha }, token);
      window.alert("Senha atualizada.");
    } catch (e) {
      window.alert(e.message);
    }
  };

  const desativar2fa = async (u) => {
    if (!window.confirm(`Desativar a verificação em duas etapas de ${u.nome}? Use isso se a pessoa perdeu acesso ao app autenticador.`)) return;
    try {
      await apiFetch("POST", `/api/usuarios/${u.id}/desativar-2fa`, {}, token);
      carregar();
    } catch (e) {
      window.alert(e.message);
    }
  };

  const abrirEdicaoPerfil = (u) => {
    setEditandoPerfil(u);
    setNovoPerfil(u.perfil);
    setNovoPodeAdmin(!!u.pode_admin_empresa);
  };

  const salvarPerfil = async () => {
    setSalvandoPerfil(true);
    try {
      await apiFetch(
        "PUT",
        `/api/usuarios/${editandoPerfil.id}`,
        { perfil: novoPerfil, pode_admin_empresa: novoPerfil === "admin_empresa" ? false : novoPodeAdmin },
        token
      );
      setEditandoPerfil(null);
      carregar();
    } catch (e) {
      window.alert(e.message);
    } finally {
      setSalvandoPerfil(false);
    }
  };

  const usuariosFiltrados = usuarios.filter((u) => {
    if (filtroNome && !u.nome.toLowerCase().includes(filtroNome.toLowerCase())) return false;
    if (filtroEmail && !u.email.toLowerCase().includes(filtroEmail.toLowerCase())) return false;
    if (filtroPerfil && u.perfil !== filtroPerfil) return false;
    if (filtroStatus && (filtroStatus === "ativo") !== !!u.ativo) return false;
    return true;
  });
  const filtrosAtivos = Boolean(filtroNome || filtroEmail || filtroPerfil || filtroStatus);
  const limparFiltros = () => { setFiltroNome(""); setFiltroEmail(""); setFiltroPerfil(""); setFiltroStatus(""); };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.text, margin: "0 0 4px", letterSpacing: "-0.02em" }}>
            Usuários
          </h1>
          <p style={{ fontSize: 13.5, color: COLORS.textMid, margin: 0 }}>
            Gerencie quem tem acesso ao painel e ao app da sua empresa.
          </p>
        </div>
        <Btn onClick={() => { setForm(VAZIO); setErro(""); setConflito(null); setModalOpen(true); }}>+ Novo usuário</Btn>
      </div>

      {usuarios.length > 0 && (
        <BarraResumoFiltro
          total={usuariosFiltrados.length}
          totalGeral={usuarios.length}
          unidade="usuário(s)"
          filtrosAtivos={filtrosAtivos}
          onLimpar={limparFiltros}
        />
      )}

      <Card style={{ padding: 0 }}>
        {loading ? (
          <EstadoCarregando />
        ) : usuarios.length === 0 ? (
          <EstadoVazio icon="users" texto="Nenhum usuário cadastrado ainda." />
        ) : usuariosFiltrados.length === 0 ? (
          <EstadoVazio icon="users" texto="Nenhum usuário encontrado com esse filtro." />
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                <ThFiltroTexto label="Nome" value={filtroNome} onChange={setFiltroNome} placeholder="Buscar..." minWidth={150} />
                <ThFiltroTexto label="E-mail" value={filtroEmail} onChange={setFiltroEmail} placeholder="Buscar..." minWidth={170} />
                <ThFiltroSelect
                  label="Perfil" value={filtroPerfil} onChange={setFiltroPerfil} minWidth={140}
                  opcoes={[{ value: "", label: "Todos" }, ...Object.entries(PERFIL_LABEL).map(([value, label]) => ({ value, label }))]}
                />
                <ThFiltroSelect
                  label="Status" value={filtroStatus} onChange={setFiltroStatus} minWidth={110}
                  opcoes={[{ value: "", label: "Todos" }, { value: "ativo", label: "Ativo" }, { value: "inativo", label: "Inativo" }]}
                />
                <ThSimples label="" />
              </tr>
            </thead>
            <tbody>
              {usuariosFiltrados.map((u) => (
                <tr key={u.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <td style={{ padding: "14px 20px", fontSize: 13.5, fontWeight: 700, color: COLORS.text }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Avatar nome={u.nome} size={28} />
                      {u.nome}
                    </div>
                  </td>
                  <td style={{ padding: "14px 20px", fontSize: 13.5, color: COLORS.textMid }}>{u.email}</td>
                  <td style={{ padding: "14px 20px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <Badge tone={PERFIL_TONE[u.perfil] || "neutral"}>{PERFIL_LABEL[u.perfil] || u.perfil}</Badge>
                      {u.pode_admin_empresa && u.perfil !== "admin_empresa" && (
                        <Badge tone="warning">+ Admin</Badge>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: "14px 20px" }}>
                    <Badge tone={u.ativo ? "success" : "neutral"}>{u.ativo ? "Ativo" : "Inativo"}</Badge>
                  </td>
                  <td style={{ padding: "14px 20px", textAlign: "right", whiteSpace: "nowrap" }}>
                    <button
                      onClick={() => abrirEdicaoPerfil(u)}
                      style={{ background: "transparent", border: `1.5px solid ${COLORS.border}`, borderRadius: 8, padding: "6px 12px", fontSize: 12.5, fontWeight: 600, color: COLORS.textMid, cursor: "pointer", marginRight: 8 }}
                    >
                      Editar perfil
                    </button>
                    <button
                      onClick={() => resetarSenha(u)}
                      style={{ background: "transparent", border: `1.5px solid ${COLORS.border}`, borderRadius: 8, padding: "6px 12px", fontSize: 12.5, fontWeight: 600, color: COLORS.textMid, cursor: "pointer", marginRight: 8 }}
                    >
                      Resetar senha
                    </button>
                    <button
                      onClick={() => alternarAtivo(u)}
                      style={{ background: "transparent", border: `1.5px solid ${COLORS.border}`, borderRadius: 8, padding: "6px 12px", fontSize: 12.5, fontWeight: 600, color: COLORS.textMid, cursor: "pointer" }}
                    >
                      {u.ativo ? "Desativar" : "Ativar"}
                    </button>
                    {u.totp_ativo && (
                      <button
                        onClick={() => desativar2fa(u)}
                        style={{ background: "transparent", border: `1.5px solid ${COLORS.border}`, borderRadius: 8, padding: "6px 12px", fontSize: 12.5, fontWeight: 600, color: COLORS.textMid, cursor: "pointer", marginLeft: 8 }}
                      >
                        Desativar 2FA
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Novo usuário" width={420}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Input label="Nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Nome completo" />
          <Input label="E-mail" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@empresa.com.br" />
          <Input label="Senha" type="password" value={form.senha} onChange={(e) => setForm({ ...form, senha: e.target.value })} placeholder="••••••••" />
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMid, display: "block", marginBottom: 6 }}>Perfil</label>
            <select
              value={form.perfil}
              onChange={(e) => setForm({ ...form, perfil: e.target.value })}
              style={{ width: "100%", border: `1.5px solid ${COLORS.border}`, borderRadius: 10, padding: "11px 14px", fontSize: 14, background: COLORS.bg, color: COLORS.text }}
            >
              <option value="funcionario">Funcionário</option>
              <option value="gestor">Gestor</option>
              <option value="admin_empresa">Administrador</option>
            </select>
          </div>

          {erro && (
            <div style={{ color: COLORS.danger, fontSize: 13, background: COLORS.dangerBg, border: "1px solid #FECACA", borderRadius: 8, padding: "9px 13px" }}>
              {erro}
              {conflito && (
                <>
                  {" "}
                  <Link
                    to={`/funcionarios?funcionario_id=${conflito.funcionarioId}`}
                    style={{ color: COLORS.primary, fontWeight: 700 }}
                  >
                    Editar cadastro de {conflito.nome}
                  </Link>
                </>
              )}
            </div>
          )}

          <Btn onClick={salvar} disabled={salvando} style={{ width: "100%", marginTop: 4 }}>
            {salvando ? "Salvando..." : "Criar usuário"}
          </Btn>
        </div>
      </Modal>

      <Modal open={!!editandoPerfil} onClose={() => setEditandoPerfil(null)} title={`Editar perfil — ${editandoPerfil?.nome || ""}`} width={380}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMid, display: "block", marginBottom: 6 }}>Perfil</label>
            <select
              value={novoPerfil}
              onChange={(e) => setNovoPerfil(e.target.value)}
              style={{ width: "100%", border: `1.5px solid ${COLORS.border}`, borderRadius: 10, padding: "11px 14px", fontSize: 14, background: COLORS.bg, color: COLORS.text }}
            >
              <option value="funcionario">Funcionário</option>
              <option value="gestor">Gestor</option>
              <option value="admin_empresa">Administrador</option>
            </select>
            <p style={{ fontSize: 11.5, color: COLORS.textLight, margin: "6px 0 0" }}>
              Tirar alguém de Gestor não apaga o histórico de aprovações que essa pessoa já fez — só muda o que ela consegue acessar a partir de agora.
            </p>
          </div>

          {novoPerfil !== "admin_empresa" && (
            <label style={{ display: "flex", alignItems: "flex-start", gap: 9, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={novoPodeAdmin}
                onChange={(e) => setNovoPodeAdmin(e.target.checked)}
                style={{ marginTop: 2 }}
              />
              <span style={{ fontSize: 13, color: COLORS.text }}>
                Também pode acessar o modo Administrador
                <span style={{ display: "block", fontSize: 11.5, color: COLORS.textLight, marginTop: 2 }}>
                  Um botão aparece no menu dessa pessoa pra trocar pro modo administrativo (Postos, Feriados, Usuários, Cadastro) sem precisar sair e entrar de novo.
                </span>
              </span>
            </label>
          )}

          <Btn onClick={salvarPerfil} disabled={salvandoPerfil} style={{ width: "100%" }}>
            {salvandoPerfil ? "Salvando..." : "Salvar"}
          </Btn>
        </div>
      </Modal>
    </div>
  );
}
